import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import AuthShell from "@/components/AuthShell";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/request-password-reset", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Trouble signing in?"
      title="Reset your password."
      subtitle={sent ? null : "Enter your email and we'll send you a link to choose a new one."}
    >
      {sent ? (
        <div data-testid="forgot-password-sent" className="space-y-4">
          <p style={{ color: "var(--ink-soft)" }}>
            If an account exists for <strong>{email}</strong>, we just sent a reset link.
            Check your inbox (and spam folder) — the link is good for the next 60 minutes.
          </p>
          <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
            Didn&apos;t receive it? Double-check the email address and{" "}
            <button
              type="button"
              data-testid="forgot-password-retry"
              onClick={() => { setSent(false); setEmail(""); }}
              className="underline"
            >
              try again
            </button>.
          </p>
          <div className="pt-2">
            <Link to="/login" data-testid="forgot-password-back-to-login" className="underline text-sm">
              Back to sign in
            </Link>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-widest">Email</Label>
            <Input
              id="email"
              data-testid="forgot-password-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              className="mt-2"
            />
          </div>
          {error && (
            <div data-testid="forgot-password-error" className="text-sm" style={{ color: "var(--terracotta)" }}>
              {error}
            </div>
          )}
          <Button
            type="submit"
            data-testid="forgot-password-submit"
            disabled={busy}
            className="btn-primary w-full py-6 rounded-full"
          >
            {busy ? "Sending link…" : "Email me a reset link"}
          </Button>
          <div className="text-sm text-center" style={{ color: "var(--ink-soft)" }}>
            Remembered it?{" "}
            <Link to="/login" data-testid="forgot-password-to-login" className="underline">
              Back to sign in
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
